import React from "react";
import { ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-serif text-2xl sm:text-3xl text-[#1F2E4D] leading-tight">
      {title}
    </h2>
    <div className="text-[15px] text-[#4A5568] leading-relaxed mt-3 space-y-3">
      {children}
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export const PrivacyPolicy = ({ onBack }) => {
  const lastUpdated = "December 2025";
  return (
    <main data-testid="privacy-view" className="hr-paper-bg">
      <section className="max-w-2xl mx-auto px-6 sm:px-8 pt-6 pb-24">
        <button
          type="button"
          onClick={onBack}
          data-testid="privacy-back-btn"
          className="inline-flex items-center gap-2 text-sm text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back
        </button>

        <header className="mt-8">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#1F2E4D]">
            Legal
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-[#0E7C7B] mt-3">
            Privacy Policy.
          </h1>
          <p className="text-sm text-[#4A5568] mt-3">
            Last updated: {lastUpdated}
          </p>
          <p className="text-base text-[#4A5568] mt-5 leading-relaxed">
            Heart Reach Counselling Services ("we", "us", "our") respects your
            privacy. This Privacy Policy explains what personal data we collect,
            how we use it, the legal bases on which we rely, and the rights
            available to you under the United Kingdom General Data Protection
            Regulation (UK GDPR), the UK Data Protection Act 2018, and
            Zimbabwe's Cyber and Data Protection Act [Chapter 12:07] (2021).
          </p>
        </header>

        <Section title="1. Who we are">
          <p>
            Heart Reach Counselling Services provides confidential, professional
            telephone-based counselling for people in Zimbabwe, supported by
            family and corporate sponsors abroad.
          </p>
          <p>
            For any data-protection questions you can contact us at{" "}
            <a
              href="mailto:admin@heartreachcounselling.com"
              className="text-[#0E7C7B] underline underline-offset-4"
            >
              admin@heartreachcounselling.com
            </a>
            .
          </p>
        </Section>

        <Section title="2. The information we collect">
          <Bullets
            items={[
              "Identity & contact data — first name, contact number, email address, and (for sponsors) the same details for the person you wish to sponsor.",
              "Service-preference data — your location, who the booking is for, preferred contact channel (WhatsApp / regular line), and the bundle you have expressed interest in.",
              "Consent records — your suitability acknowledgement, data-use consent, data-sharing consent, and acknowledgement of your data-protection rights.",
              "Care-related data shared during your suitability call and during any sessions, processed strictly in line with professional counselling standards.",
              "Technical data — basic usage information that may be processed by your browser (e.g. essential cookies, see our Cookies Policy).",
            ]}
          />
        </Section>

        <Section title="3. How we use your information">
          <Bullets
            items={[
              "To arrange and deliver telephone counselling sessions you (or your sponsor) have requested.",
              "To carry out the free suitability assessment before any billing.",
              "To send you administrative communications about your booking, payment instructions, and scheduled sessions.",
              "To confirm attendance to sponsors who have opted in to receive updates — never the content of sessions.",
              "To respond to corporate-partnership and general enquiries.",
              "To comply with legal, regulatory, and professional record-keeping obligations.",
            ]}
          />
        </Section>

        <Section title="4. Legal bases for processing">
          <Bullets
            items={[
              "Consent (Art. 6(1)(a) UK GDPR; s.13 Zimbabwe Cyber and Data Protection Act) for processing your data for the purposes set out above and for any special-category health-related data shared during sessions (Art. 9(2)(a)).",
              "Contract (Art. 6(1)(b) UK GDPR) where processing is necessary to provide the counselling service you have requested.",
              "Legitimate interests (Art. 6(1)(f) UK GDPR) for safeguarding, fraud prevention, and the secure operation of our service.",
              "Legal obligation (Art. 6(1)(c) UK GDPR) where we must retain certain records for regulatory or tax purposes.",
            ]}
          />
        </Section>

        <Section title="5. Sharing your information">
          <p>
            We only share personal data where it is necessary to deliver the
            service, where you have consented, or where we are required by law.
            Categories of recipient include:
          </p>
          <Bullets
            items={[
              "Vetted, qualified counsellors engaged to provide your sessions.",
              "Carefully selected processors (e.g. our hosting, email, and payment providers) under written data-processing agreements.",
              "Regulators, professional bodies, law-enforcement, or courts where we are legally compelled to do so.",
              "We do not sell your data, and we do not share session content with sponsors.",
            ]}
          />
        </Section>

        <Section title="6. International transfers">
          <p>
            Because our service connects Zimbabwe and the UK / diaspora, your
            data may be transferred between Zimbabwe, the United Kingdom, and
            other jurisdictions where our processors operate. Where transfers
            leave the UK or Zimbabwe, we rely on appropriate safeguards such as
            the UK International Data Transfer Agreement, the UK Addendum to the
            EU Standard Contractual Clauses, or equivalent contractual measures
            under Zimbabwe's Cyber and Data Protection Act (2021).
          </p>
        </Section>

        <Section title="7. How long we keep your data">
          <p>
            We retain your data only for as long as is necessary for the
            purposes set out in this policy. Clinical and booking records are
            held in line with professional record-keeping requirements and
            applicable law; enquiry data is held for a maximum of 24 months
            from your last contact, unless a longer period is required by law.
          </p>
        </Section>

        <Section title="8. Your rights">
          <p>Under UK GDPR and the Zimbabwe Cyber and Data Protection Act you have the right to:</p>
          <Bullets
            items={[
              "Request access to the personal data we hold about you.",
              "Request correction of inaccurate or incomplete data.",
              "Request deletion of your data, subject to legal record-keeping obligations.",
              "Restrict or object to certain processing.",
              "Withdraw consent at any time, without affecting the lawfulness of processing carried out before withdrawal.",
              "Lodge a complaint with the UK Information Commissioner's Office (ico.org.uk) or with Zimbabwe's Data Protection Authority (POTRAZ).",
            ]}
          />
          <p>
            To exercise any of these rights, email{" "}
            <a
              href="mailto:admin@heartreachcounselling.com"
              className="text-[#0E7C7B] underline underline-offset-4"
            >
              admin@heartreachcounselling.com
            </a>
            . We will respond within one calendar month.
          </p>
        </Section>

        <Section title="9. Security">
          <p>
            We use appropriate technical and organisational measures —
            including encryption in transit, restricted access, vetted
            counsellors, and secure hosting — to protect your data from
            unauthorised access, loss, or disclosure.
          </p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. Material
            changes will be highlighted on this page and, where appropriate,
            communicated to you directly. The "Last updated" date above
            indicates the latest revision.
          </p>
        </Section>

        <p className="text-xs text-[#4A5568] mt-12 leading-relaxed">
          This document is provided as a clear, accessible summary of our
          practices and does not replace individual legal advice tailored to
          your circumstances.
        </p>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
